/* ─────────────────────────────────────────────────────────────────────────
   Locales — the language a page is written in and the region it is priced for.

   A locale is a language plus a region: `en-gb`, `fr-ca`, `pt-br`. The
   language picks the catalogue the SPA renders from (web/src/i18n); the region
   picks the currency and tax line through ./pricing.ts. They are one value on
   purpose: a visitor changes both with one control and never lands on French
   copy with Brazilian prices by accident.

   The tag is the URL prefix (`/en-gb/contact`) and the value of the
   `mauvely_locale` cookie. Lowercase throughout, so a URL has one spelling.

   ── Two questions, two answers ──────────────────────────────────────────────
   `bestLocale` answers "which locale should this visitor read?" and is led by
   Accept-Language: a Briton in Boston keeps British English. `regionLocale`
   answers "which locale matches where this visitor is?" and is led by the
   country: the same Briton is offered `en-us` in a banner, and switching is one
   press. The redirect uses the first; the banner compares the two.

   ── Why en-ie ────────────────────────────────────────────────────────────────
   Every other euro-region locale is non-English. Without it an English speaker
   in Berlin gets German copy or pounds, and the pricing page's own region
   selector — the escape hatch — is gone now that the locale carries the region.
   ───────────────────────────────────────────────────────────────────────── */

// ./regions rather than ./pricing: the region key and the country table are
// all this file needs, and importing them from pricing.ts would drag five
// hundred lines of plans and tax lines into every repository that holds a
// copy of this one.
import { COUNTRY_TO_REGION, type RegionKey } from './regions';

export type Language = 'en' | 'fr' | 'de' | 'es' | 'it' | 'nl' | 'pt' | 'ja' | 'tr';

export interface Locale {
  /** The URL prefix and cookie value: `en-gb`. */
  tag: string;
  /** Which catalogue directory under web/src/i18n/catalogues. */
  language: Language;
  /** Which prices, currency and tax line. */
  regionKey: RegionKey;
  /** What `Intl` is given for dates and money: `en-GB`. */
  intl: string;
  /** The name a speaker of that language would pick from a list. */
  native: string;
}

/**
 * Ordered so the first row for each language is that language's home: the
 * fallback when Accept-Language names the language and nothing else fits.
 */
export const LOCALES: readonly Locale[] = [
  { tag: 'en-gb', language: 'en', regionKey: 'GB', intl: 'en-GB', native: 'English (UK)' },
  { tag: 'en-us', language: 'en', regionKey: 'US', intl: 'en-US', native: 'English (US)' },
  { tag: 'en-ie', language: 'en', regionKey: 'EU', intl: 'en-IE', native: 'English (Europe)' },
  { tag: 'en-ca', language: 'en', regionKey: 'CA', intl: 'en-CA', native: 'English (Canada)' },
  { tag: 'en-au', language: 'en', regionKey: 'AU', intl: 'en-AU', native: 'English (Australia)' },
  { tag: 'en-in', language: 'en', regionKey: 'IN', intl: 'en-IN', native: 'English (India)' },
  { tag: 'fr-fr', language: 'fr', regionKey: 'EU', intl: 'fr-FR', native: 'Français (France)' },
  { tag: 'fr-ca', language: 'fr', regionKey: 'CA', intl: 'fr-CA', native: 'Français (Canada)' },
  { tag: 'de-de', language: 'de', regionKey: 'EU', intl: 'de-DE', native: 'Deutsch' },
  { tag: 'es-es', language: 'es', regionKey: 'EU', intl: 'es-ES', native: 'Español' },
  { tag: 'it-it', language: 'it', regionKey: 'EU', intl: 'it-IT', native: 'Italiano' },
  { tag: 'nl-nl', language: 'nl', regionKey: 'EU', intl: 'nl-NL', native: 'Nederlands' },
  { tag: 'pt-pt', language: 'pt', regionKey: 'EU', intl: 'pt-PT', native: 'Português (Portugal)' },
  { tag: 'pt-br', language: 'pt', regionKey: 'BR', intl: 'pt-BR', native: 'Português (Brasil)' },
  { tag: 'ja-jp', language: 'ja', regionKey: 'JP', intl: 'ja-JP', native: '日本語' },
  // Priced in euros: there is no lira region, and the EU region already
  // stands in for the rest of Europe on its own currency (shared/pricing.ts).
  { tag: 'tr-tr', language: 'tr', regionKey: 'EU', intl: 'tr-TR', native: 'Türkçe' }
];

export const DEFAULT_LOCALE = 'en-gb';

/** A year, like `mauvely_privacy`. */
export const LOCALE_COOKIE = 'mauvely_locale';
export const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * The `Domain=` attribute the locale cookie is written with, or nothing.
 *
 * Lives here, beside the cookie's name and lifetime, because it is part of the
 * same fact: a locale chosen on mauvely.com has to be readable on
 * status.mauvely.com, and that only works if the cookie is written for the
 * parent domain. Every site that gains locales needs this rule and must not
 * write its own copy of it.
 *
 * Empty off the real domain, so a localhost or preview deployment writes a
 * host-only cookie rather than one for a domain it is not on — which the
 * browser would reject outright.
 */
export function cookieDomainAttr(hostname: string): string {
  return hostname === 'mauvely.com' || hostname.endsWith('.mauvely.com')
    ? '; Domain=.mauvely.com'
    : '';
}

const BY_TAG = new Map<string, Locale>(LOCALES.map((l) => [l.tag, l]));

export function isLocale(value: unknown): value is string {
  return typeof value === 'string' && BY_TAG.has(value);
}

/** The row for a tag. An unknown tag reads as the default rather than throwing,
 *  because every caller has already been through `isLocale` or a redirect. */
export function localeOf(tag: string): Locale {
  return BY_TAG.get(tag) ?? BY_TAG.get(DEFAULT_LOCALE)!;
}

/** Locales that render from the same catalogue, the language's home first. */
export function localesForLanguage(language: Language): Locale[] {
  return LOCALES.filter((l) => l.language === language);
}

/**
 * Take a locale prefix off a pathname.
 *
 * `/en-gb` and `/en-gb/` both give `rest: '/'`, because the home page compares
 * `path === '/'` and would otherwise lose its own nav. Anything that is not a
 * known tag — `/contact`, `/o/acme/signin`, `/zz-zz/x` — is returned whole.
 */
export function splitLocale(pathname: string): { locale: string | null; rest: string } {
  const m = /^\/([a-z]{2}-[a-z]{2})(\/.*)?$/.exec(pathname);
  if (!m || !isLocale(m[1])) return { locale: null, rest: pathname || '/' };
  return { locale: m[1], rest: m[2] || '/' };
}

/**
 * Put a locale in front of an internal path, unless it should not have one.
 *
 * Absolute URLs, protocol-relative URLs, the API, `/dl/*` and `/.well-known/*`
 * pass through untouched, as does a path that already carries a locale. The
 * query string, if any, rides along.
 */
export function localePath(locale: string, to: string): string {
  if (!to.startsWith('/') || to.startsWith('//')) return to;
  if (to.startsWith('/api/') || to.startsWith('/dl/') || to.startsWith('/.well-known/')) return to;
  const pathOnly = to.split(/[?#]/, 1)[0] ?? to;
  if (splitLocale(pathOnly).locale) return to;
  return `/${locale}${to}`;
}

interface LanguageTag {
  language: string;
  region: string | null;
  q: number;
}

/** `de-DE,de;q=0.9,en;q=0.8` → ordered by preference. `*` and rubbish are dropped. */
export function parseAcceptLanguage(header: string | null | undefined): LanguageTag[] {
  if (!header) return [];
  const out: LanguageTag[] = [];
  for (const part of header.split(',')) {
    const [rawTag, ...params] = part.trim().split(';');
    const tag = (rawTag ?? '').trim().toLowerCase();
    if (!/^[a-z]{2,3}(-[a-z0-9]{2,8})*$/.test(tag)) continue;
    let q = 1;
    for (const p of params) {
      const m = /^\s*q\s*=\s*(0(\.\d{1,3})?|1(\.0{1,3})?)\s*$/i.exec(p);
      // `m[1] ?? ''` rather than `m[1]!`: this file is copied into repositories
      // whose tsconfig sets `noUncheckedIndexedAccess`, where a capture group is
      // `string | undefined` however certain the regex makes it. The regex cannot
      // match without group 1, so the fallback is unreachable — and NaN would fail
      // the `q <= 0` test below and drop the tag, which is the safe direction.
      if (m) q = Number.parseFloat(m[1] ?? '');
    }
    if (q <= 0) continue;
    const [language, ...subtags] = tag.split('-');
    const region = subtags.find((s) => /^[a-z]{2}$/.test(s)) ?? null;
    out.push({ language: language!, region: region ? region.toUpperCase() : null, q });
  }
  // Stable, so equal weights keep the header's own order.
  return out
    .map((t, i) => ({ t, i }))
    .sort((a, b) => b.t.q - a.t.q || a.i - b.i)
    .map(({ t }) => t);
}

const regionOfCountry = (country: string | null | undefined): RegionKey | null =>
  country ? (COUNTRY_TO_REGION[country.toUpperCase()] ?? null) : null;

/**
 * Which locale a visitor should read. Led by their language, so a saved choice
 * or an Accept-Language wins over where the request came from.
 *
 *   1. a valid `mauvely_locale` cookie
 *   2. an exact tag from Accept-Language (`fr-CA` → `fr-ca`)
 *   3. a language from Accept-Language, in the region the country maps to
 *   4. that language's home locale
 *   5. English for the country's region
 *   6. the default
 *
 * Always returns a tag `isLocale` accepts, so a redirect built from it cannot
 * loop.
 */
export function bestLocale(input: {
  cookie?: string | null;
  acceptLanguage?: string | null;
  country?: string | null;
}): string {
  if (isLocale(input.cookie)) return input.cookie;

  const langs = parseAcceptLanguage(input.acceptLanguage);
  const region = regionOfCountry(input.country);

  for (const l of langs) {
    if (l.region) {
      const tag = `${l.language}-${l.region.toLowerCase()}`;
      if (isLocale(tag)) return tag;
    }
  }
  for (const l of langs) {
    const hit = region && LOCALES.find((x) => x.language === l.language && x.regionKey === region);
    if (hit) return hit.tag;
  }
  for (const l of langs) {
    const hit = LOCALES.find((x) => x.language === l.language);
    if (hit) return hit.tag;
  }
  if (region) {
    const hit = LOCALES.find((x) => x.language === 'en' && x.regionKey === region);
    if (hit) return hit.tag;
  }
  return DEFAULT_LOCALE;
}

/**
 * Which locale matches where the visitor is. Led by the country, so the banner
 * can ask "switch to your region?" when this differs from what they are
 * reading. Null when the country is unknown or unmapped: no region, no banner.
 *
 * The language is still theirs where the region has it — a French speaker in
 * Canada is offered `fr-ca`, not `en-ca` — and falls back to English for the
 * region, then to the region's only locale (Brazil, Japan).
 */
export function regionLocale(input: {
  acceptLanguage?: string | null;
  country?: string | null;
}): string | null {
  const region = regionOfCountry(input.country);
  if (!region) return null;
  const inRegion = LOCALES.filter((x) => x.regionKey === region);
  if (!inRegion.length) return null;
  for (const l of parseAcceptLanguage(input.acceptLanguage)) {
    const hit = inRegion.find((x) => x.language === l.language);
    if (hit) return hit.tag;
  }
  return (inRegion.find((x) => x.language === 'en') ?? inRegion[0]!).tag;
}
